import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  BookOpen, 
  FileText, 
  Users, 
  Settings,
  ChevronLeft,
  ArrowRight
} from 'lucide-react';
import Navigation from '../Navigation';

/**
 * AdminGuides Component
 * 
 * Landing page for admin documentation guides.
 * Displays cards for each available guide with navigation to individual guide views.
 * 
 * Features:
 * - Card-based layout similar to Resources page
 * - Direct navigation to individual guides
 * - Admin-only access with role protection
 * - Responsive design
 */
const AdminGuides = () => {
  const navigate = useNavigate();

  // Static list of available admin guides
  const guides = [
    {
      id: 'markdown-formatting',
      title: 'Markdown Formatting Guide',
      description: 'Learn how to format markdown content for resources and guides with proper styling, tables, and structure.',
      icon: FileText,
      color: 'bg-blue-500',
      slug: 'markdown-formatting'
    },
    {
      id: 'managing-resources',
      title: 'Managing Resources',
      description: 'Complete guide on creating, editing, and organizing resources and guides in the admin panel.',
      icon: BookOpen,
      color: 'bg-green-500',
      slug: 'managing-resources'
    },
    {
      id: 'managing-users',
      title: 'Managing Users',
      description: 'Learn how to create, edit, and manage user accounts, roles, and permissions.',
      icon: Users,
      color: 'bg-purple-500',
      slug: 'managing-users'
    }
  ];

  const handleGuideClick = (slug) => {
    navigate(`/admin/guides/${slug}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Navigation />
      
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="mr-4"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Admin Dashboard
              </Button>
            </div>
            
            <div className="flex items-center mb-4">
              <Settings className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Admin Guides</h1>
                <p className="text-gray-600 mt-1">
                  Documentation and guides for administrators
                </p>
              </div>
            </div>
            
            {/* Breadcrumb */}
            <div className="text-sm text-gray-500">
              Admin Dashboard / Admin Guides
            </div>
          </div>

          {/* Guides Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guides.map((guide) => {
              const IconComponent = guide.icon;
              return (
                <Card 
                  key={guide.id}
                  className="group hover:shadow-lg transition-all duration-200 cursor-pointer border-0 shadow-md hover:shadow-xl"
                  onClick={() => handleGuideClick(guide.slug)}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div className={`w-12 h-12 ${guide.color} rounded-lg flex items-center justify-center`}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                    </div>
                    <CardTitle className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {guide.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed">
                      {guide.description}
                    </CardDescription>
                    <div className="mt-4">
                      <Button 
                        variant="outline" 
                        className="w-full group-hover:bg-blue-50 group-hover:border-blue-200 transition-colors"
                      >
                        View Guide
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Additional Info */}
          <div className="mt-12 bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <BookOpen className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  About Admin Guides
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  These guides provide comprehensive documentation for administrators on how to 
                  manage the IAS Portal effectively. Each guide covers specific aspects of 
                  system administration, from content management to user administration.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminGuides;
